import React, { useState } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  Typography,
  Paper,
  Box,
  TextField,
  Button,
  Grid2,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  IconButton,
  Backdrop,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Security as SecurityIcon,
  Person as PersonIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import PageHeader from '../../components/PageHeader';
import { httpService } from '../../services/httpService';
import { decryptResponse, encryptResponse, validatePasswordStrong } from '../../services';
import { useSelector, useDispatch } from 'react-redux';
import { setUserDetails } from '../../features/auth/authSlice';
import { API_BASE_URL, REQ_ENC_KEY } from '../../services/api';
import OtpInput from '../../components/common/OtpInput';

interface UserProfile {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  position: string;
  bio: string;
  avatar: string;
}

const Profile: React.FC = () => {
  const dispatch = useDispatch();
  const userDetails = useSelector((state: any) => state.auth.userDetails);

  // Initialize profile from Redux userDetails when available
  const [profile, setProfile] = useState<UserProfile>({
    firstName: userDetails?.user_full_name
      ? userDetails.user_full_name.split(' ')[0] || ''
      : 'John',
    lastName: userDetails?.user_full_name
      ? userDetails.user_full_name.split(' ').slice(1).join(' ') || ''
      : 'Doe',
    email: userDetails?.email_id || 'john.doe@example.com',
    phone: userDetails?.mobile_no || '+1 (555) 123-4567',
    company: userDetails?.organization_name || 'NetApp Inc',
    position: userDetails?.department || 'Software Engineer',
    bio: '',
    avatar: '',
  });

  const [isEditing, setIsEditing] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordTouched, setPasswordTouched] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Email OTP (MFA) verification before changing the password.
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [isOtpPopupOpen, setIsOtpPopupOpen] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);

  const handleProfileChange = (field: keyof UserProfile) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setProfile(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSaveProfile = () => {
    // Update only the allowed fields in Redux: mobile number, user name, department
    const updatedUserDetails = {
      ...userDetails,
      mobile_no: profile.phone,
      user_full_name: `${profile.firstName} ${profile.lastName}`.trim(),
      department: profile.position,
    };

    // Dispatch to Redux
    dispatch(setUserDetails(updatedUserDetails));

    // Simulate API call + save UX
    setTimeout(() => {
      setIsEditing(false);
      setShowSuccessAlert(true);
      setTimeout(() => setShowSuccessAlert(false), 3000);
    }, 500);
  };

  const handleOtpPopupClose = () => {
    setIsOtpPopupOpen(false);
    setOtp('');
    setOtpError('');
  };

  // Step 1: validate the form, then send an OTP to the user's email before changing the password.
  const handlePasswordChange = async () => {
    // Strong password validation (same rules as the rest of the app).
    const newErrors = {
      currentPassword: passwordData.currentPassword ? '' : 'Current password is required',
      newPassword: validatePasswordStrong(passwordData.newPassword) || '',
      confirmPassword: !passwordData.confirmPassword
        ? 'Please confirm your password'
        : (passwordData.newPassword !== passwordData.confirmPassword ? 'Passwords do not match' : ''),
    };

    setPasswordErrors(newErrors);
    setPasswordTouched({ currentPassword: true, newPassword: true, confirmPassword: true });

    // If any errors, focus the first invalid field and abort
    const firstErrorKey = Object.keys(newErrors).find(k => (newErrors as any)[k]);
    if (firstErrorKey) {
      const idMap: any = {
        currentPassword: 'current-password-field',
        newPassword: 'new-password-field',
        confirmPassword: 'confirm-password-field',
      };
      const el = document.getElementById(idMap[firstErrorKey]);
      el?.focus();
      return;
    }

    const emailId = userDetails?.email_id || profile.email;
    if (!emailId) {
      toast.error('Unable to find your email for verification.', { position: 'top-right', autoClose: 1500 });
      return;
    }

    setIsSendingOtp(true);
    setOtp('');
    setOtpError('');
    const payload = {
      request_id: 'change_password_otp',
      action: 'send_otp',
      data: await encryptResponse({
        email_id: emailId,
        purpose: 'mfa'
      }, REQ_ENC_KEY)
    };

    try {
      const response = await httpService.post<any>(`${API_BASE_URL}/user/sendOTP`, payload);
      if (response.data.status === 200) {
        setIsOtpPopupOpen(true);
        toast.success('OTP sent successfully!', { position: 'top-right', autoClose: 1500 });
      } else {
        const message = response.data?.error?.errorDetails || 'Failed to send OTP. Please try again.';
        toast.error(message, { position: 'top-right', autoClose: 1500 });
      }
    } catch (error) {
      console.log(error);
      toast.error('An error occurred while sending OTP.', { position: 'top-right', autoClose: 1500 });
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Step 2: verify the OTP, then perform the actual password change.
  const verifyOtp = async () => {
    if (otp.length !== 6) {
      setOtpError('Please enter the 6-digit OTP.');
      return;
    }

    const emailId = userDetails?.email_id || profile.email;
    const payload = {
      request_id: 'change_password_verify_otp',
      action: 'verify_otp',
      data: await encryptResponse({
        email_id: emailId,
        otp: otp
      }, REQ_ENC_KEY)
    };

    try {
      const response = await httpService.post<any>(`${API_BASE_URL}/user/verifyOTP`, payload);
      if (response.data.status === 200 && response.data.data) {
        const decryptedData = await decryptResponse(response.data.data, REQ_ENC_KEY);
        const is_email_verified = (decryptedData as any).is_email_verified;
        setOtpError('');
        setIsOtpPopupOpen(false);
        setOtp('');
        await performPasswordChange(is_email_verified);
      } else {
        setOtpError('Invalid OTP. Please try again.');
      }
    } catch (error) {
      console.log(error);
      setOtpError('An error occurred while verifying OTP.');
    }
  };

  const performPasswordChange = async (is_email_verified: any) => {
    const payload = {
      data: await encryptResponse({
        email_id: userDetails?.email_id || profile.email,
        old_password: passwordData.currentPassword,
        new_password: passwordData.newPassword,
        confirm_password: passwordData.confirmPassword,
        is_email_verified: is_email_verified
      }, REQ_ENC_KEY),
    };

    try {
      setPasswordLoading(true);
      const resp = await httpService.post<any>(`${API_BASE_URL}/user/changePassword`, payload);
      const respData = resp?.data;
      // Prefer explicit success check from API response body
      if (respData?.status === 200) {
        setPasswordDialogOpen(false);
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        toast.success(respData?.message || 'Password Changed Successfully', { position: 'top-right', autoClose: 1500 });
      } else {
        const message = respData?.message || 'Failed to change password';
        toast.error(message, { position: 'top-right', autoClose: 3000 });
      }
    } catch (err: any) {
      // Try to surface detailed error information from the API (eg. Old Password Incorrect)
      const apiData = err?.response?.data;
      const message = apiData?.message || apiData?.error?.errorDetails || err.message || 'Failed to change password';
      toast.error(message, { position: 'top-right', autoClose: 3000 });
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ mb: 4 }}>
        <PageHeader
          heading="Profile Settings"
          subheading="Manage your profile information, notification preferences, and security settings"
          variant="gradient"
        >
          <Chip
            label={`Role: ${userDetails?.role}`}
            color="secondary"
            variant="filled"
            size="small"
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              color: '#ffffff',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
              }
            }}
          />
        </PageHeader>
      </Box>

      {showSuccessAlert && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Settings saved successfully!
        </Alert>
      )}

      <Grid2 container spacing={3}>
        {/* Profile Information */}
        <Grid2 size={{ xs: 12 }}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Personal Information (read-only)</Typography>
              {/* <Box sx={{ ml: 'auto' }}>
                {isEditing ? (
                  <>
                    <Button
                      startIcon={<SaveIcon />}
                      variant="contained"
                      onClick={handleSaveProfile}
                      sx={{ mr: 1 }}
                    >
                      Save
                    </Button>
                    <Button
                      startIcon={<CancelIcon />}
                      variant="outlined"
                      onClick={() => setIsEditing(false)}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button
                    startIcon={<EditIcon />}
                    variant="outlined"
                    onClick={() => setIsEditing(true)}
                  >
                    Edit
                  </Button>
                )}
              </Box> */}
            </Box>

            <Grid2 container spacing={3}>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={profile.firstName}
                  onChange={handleProfileChange('firstName')}
                  disabled={!isEditing}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={profile.lastName}
                  onChange={handleProfileChange('lastName')}
                  disabled={!isEditing}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={profile.phone}
                  onChange={handleProfileChange('phone')}
                  disabled={!isEditing}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Department"
                  value={profile.position}
                  onChange={handleProfileChange('position')}
                  disabled={!isEditing}
                />
              </Grid2>
              {/* Read-only account details requested by product */}
              <Grid2 size={12}>
                <Box sx={{ mt: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>Account Details (read-only)</Typography>
                  <TextField
                    fullWidth
                    label="Organization Name"
                    value={userDetails?.organization_name || profile.company}
                    disabled
                    sx={{ mb: 1 }}
                  />
                  <TextField
                    fullWidth
                    label="Email Id"
                    value={userDetails?.email_id || profile.email}
                    disabled
                    sx={{ mb: 1 }}
                  />
                  <TextField
                    fullWidth
                    label="Account Status"
                    value={userDetails?.account_status || 'Unknown'}
                    disabled
                  />
                </Box>
              </Grid2>
            </Grid2>
          </Paper>
        </Grid2>
        {/* Security Settings */}
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <SecurityIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Security Settings</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => setPasswordDialogOpen(true)}
                sx={{ mt: 1, alignSelf: 'flex-start' }}
              >
                Change Password
              </Button>
            </Box>
          </Paper>
        </Grid2>
      </Grid2>

      {/* Password Change Dialog */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)}>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="current-password-field"
            label="Current Password"
            type={showCurrentPassword ? 'text' : 'password'}
            fullWidth
            variant="outlined"
            value={passwordData.currentPassword}
            onChange={(e) => {
              const value = e.target.value;
              setPasswordData(prev => ({ ...prev, currentPassword: value }));
              setPasswordTouched(prev => ({ ...prev, currentPassword: true }));
              setPasswordErrors(prev => ({ ...prev, currentPassword: value ? '' : 'Current password is required' }));
            }}
            onBlur={() => setPasswordTouched(prev => ({ ...prev, currentPassword: true }))}
            error={Boolean(passwordErrors.currentPassword)}
            helperText={passwordErrors.currentPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowCurrentPassword(prev => !prev)} edge="end">
                    {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            id="new-password-field"
            label="New Password"
            type={showNewPassword ? 'text' : 'password'}
            fullWidth
            variant="outlined"
            value={passwordData.newPassword}
            onChange={(e) => {
              const value = e.target.value;
              setPasswordData(prev => ({ ...prev, newPassword: value }));
              setPasswordTouched(prev => ({ ...prev, newPassword: true }));
              setPasswordErrors(prev => ({
                ...prev,
                newPassword: validatePasswordStrong(value) || '',
                // keep confirm-match error in sync while typing the new password
                confirmPassword: passwordData.confirmPassword
                  ? (value !== passwordData.confirmPassword ? 'Passwords do not match' : '')
                  : prev.confirmPassword,
              }));
            }}
            onBlur={() => setPasswordTouched(prev => ({ ...prev, newPassword: true }))}
            error={Boolean(passwordErrors.newPassword)}
            helperText={passwordErrors.newPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowNewPassword(prev => !prev)} edge="end">
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            id="confirm-password-field"
            label="Confirm New Password"
            type={showConfirmPassword ? 'text' : 'password'}
            fullWidth
            variant="outlined"
            value={passwordData.confirmPassword}
            onChange={(e) => {
              const value = e.target.value;
              setPasswordData(prev => ({ ...prev, confirmPassword: value }));
              setPasswordTouched(prev => ({ ...prev, confirmPassword: true }));
              setPasswordErrors(prev => ({
                ...prev,
                confirmPassword: !value
                  ? 'Please confirm your password'
                  : (passwordData.newPassword !== value ? 'Passwords do not match' : ''),
              }));
            }}
            onBlur={() => setPasswordTouched(prev => ({ ...prev, confirmPassword: true }))}
            error={Boolean(passwordErrors.confirmPassword)}
            helperText={passwordErrors.confirmPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowConfirmPassword(prev => !prev)} edge="end">
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)} disabled={passwordLoading || isSendingOtp}>Cancel</Button>
          <Button onClick={handlePasswordChange} variant="contained" disabled={passwordLoading || isSendingOtp}>
            {passwordLoading ? 'Changing...' : (isSendingOtp ? 'Sending OTP...' : 'Change Password')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send OTP Loader */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1, backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
        open={isSendingOtp}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Sending OTP...
          </Typography>
        </Box>
      </Backdrop>

      {/* Email OTP Verification Dialog */}
      <Dialog
        open={isOtpPopupOpen}
        onClose={handleOtpPopupClose}
        maxWidth="sm"
        fullWidth
        slotProps={{ paper: { sx: { width: '500px', maxWidth: '90vw' } } }}
      >
        <DialogTitle>Verify Email</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
            Enter the 6-digit OTP sent to <strong>{userDetails?.email_id || profile.email}</strong> to change your password.
          </Typography>
          <OtpInput
            id="change-password-otp"
            value={otp}
            onChange={(value) => {
              setOtp(value);
              if (otpError) {
                setOtpError('');
              }
            }}
            error={!!otpError}
            helperText={otpError}
            autoFocus={isOtpPopupOpen}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleOtpPopupClose} color="secondary">
            Cancel
          </Button>
          <Button onClick={verifyOtp} color="primary" disabled={otp.length !== 6}>
            Verify OTP
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Profile;
